<template>
  <div class="input-wrapper">
    <div class="input-label">
      <span v-if="required" class="required-mark">*</span>
      <label v-if="label">
        {{ label }}
      </label>
      <button
        v-if="tooltip"
        ref="tooltipTrigger"
        type="button"
        class="info-tooltip-btn"
        tabindex="-1"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
        @focus="openTooltip"
        @blur="closeTooltip"
      >
        <i class="pi pi-question-circle"></i>
      </button>
    </div>

    <div class="input-container" :class="{ 'has-error': error }">
      <input
        v-model="inputValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        autocomplete="off"
        :type="type"
        class="input"
      />
      <button
        type="button"
        class="clear-btn"
        @click="clearInput"
        :class="{ active: clearable && inputValue && !disabled }"
      >
        <i class="pi pi-times"></i>
      </button>
      <button
        v-if="appendIcon"
        type="button"
        class="append-icon-btn"
        @click="clickAppend"
        tabindex="-1"
      >
        <i :class="getAppendIconClass"></i>
      </button>
    </div>

    <span v-if="error" class="input-error">{{ error }}</span>

    <Teleport to="body">
      <transition name="tooltip-fade">
        <div
          v-if="tooltipVisible"
          class="tooltip-content"
          :style="tooltipStyle"
        >
          {{ tooltip }}
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'Input',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    appendIcon: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      value: this.modelValue,
      tooltipVisible: false,
      tooltipStyle: {},
    }
  },
  computed: {
    inputValue: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    getAppendIconClass() {
      if (this.appendIcon === 'eye') return 'pi pi-eye'
      if (this.appendIcon === 'eye-off') return 'pi pi-eye-slash'
      return this.appendIcon
    },
  },
  methods: {
    clearInput() {
      this.$emit('update:modelValue', '')
    },
    clickAppend() {
      this.$emit('click:append')
    },
    openTooltip() {
      if (!this.tooltip) return
      this.updateTooltipPosition()
      this.tooltipVisible = true
      window.addEventListener('scroll', this.closeTooltip, true)
      window.addEventListener('resize', this.closeTooltip)
    },
    closeTooltip() {
      this.tooltipVisible = false
      window.removeEventListener('scroll', this.closeTooltip, true)
      window.removeEventListener('resize', this.closeTooltip)
    },
    updateTooltipPosition() {
      const trigger = this.$refs.tooltipTrigger
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      this.tooltipStyle = {
        left: `${rect.left + rect.width / 2}px`,
        top: `${rect.top - 12}px`,
      }
    },
  },
  beforeUnmount() {
    this.closeTooltip()
  },
}
</script>

<style lang="scss" scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;

  label {
    font-weight: $font-weight-md;
    font-size: $font-size-md;
    font-size: 14px;
    color: $color-grey-3;
  }

  .info-tooltip-btn {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #9ca3af;
    font-size: 16px;
    display: flex;
    align-items: center;
    position: relative;
  }

  .required-mark {
    color: $color-red;
  }
}

// Teleported to <body> to escape the popup body's `overflow-y: auto`
// clipping — must stay a top-level scoped selector so the `data-v` match
// still applies after teleport. Positioned via inline style (icon rect).
.tooltip-content {
  position: fixed;
  transform: translate(-50%, -100%);
  padding: 12px 16px;
  background: white;
  color: $color-grey-3;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
  z-index: $popup-content-z-index + 10;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.05);
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: white;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}

.input-container {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  height: 48px;
  width: 100%;
  border: 1px solid $input-border-color;
  border-radius: $border-radius-xs;

  &.has-error {
    border-color: $color-red;
  }

  .clear-btn {
    display: none;
    background-color: transparent;
    cursor: pointer;
    padding: 0;
    border: 0;
    outline: none;
    font-size: 12px;
    color: #999999;
    margin-right: $space-xs;

    &.active {
      display: inline-block;
    }
  }

  .append-icon-btn {
    background-color: transparent;
    cursor: pointer;
    padding: 0;
    border: 0;
    outline: none;
    font-size: 16px;
    color: #999999;
    display: inline-block;
    margin-right: $space-xs;
  }
}

.input-error {
  color: $color-red;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.input {
  height: 100%;
  width: 100%;
  border: none;
  padding: $space-xs;
  border-radius: $border-radius-xs;
  outline: none;
  font-weight: 400;
  font-size: 16px;
  color: $color-default;

  &::placeholder {
    color: $input-placeholder-color;
  }

  &:disabled {
    background-color: $color-grey-2;
    color: $input-placeholder-color;
    cursor: not-allowed;
  }
}
</style>
